import React, { useCallback, useEffect, useMemo, useState } from "react";
import { handleAdminReferrals } from "../../services/APIClient";
import { useLoader } from "../../contexts/LoaderContext";

const number = (value) => new Intl.NumberFormat("en-US").format(Number(value || 0));
const date = (value) => value ? new Date(value).toLocaleString() : "—";

const statusStyle = {
  pending_payment: "border-white/10 bg-white/[0.05] text-white/65",
  holding: "border-amber-300/25 bg-amber-300/[0.08] text-amber-200",
  qualified: "border-emerald-300/25 bg-emerald-300/[0.08] text-emerald-200",
  rejected: "border-red-300/25 bg-red-300/[0.08] text-red-200",
  reversed: "border-fuchsia-300/25 bg-fuchsia-300/[0.08] text-fuchsia-200",
};

const label = (value) => String(value || "unknown").replaceAll("_", " ");

export default function Referrals() {
  const [data, setData] = useState({ enabled: false, counts: {}, rewardCounts: {}, rows: [], total: 0 });
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const { setLoading } = useLoader();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await handleAdminReferrals({ page, limit: 25, ...(status ? { status } : {}) });
      setData(response?.data || { enabled: false, counts: {}, rewardCounts: {}, rows: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, [page, setLoading, status]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(Number(data.total || 0) / 25)), [data.total]);
  const metrics = [
    ["Awaiting payment", data.counts?.pending_payment],
    ["In 30-day verification", data.counts?.holding],
    ["Verified", data.counts?.qualified],
    ["Rewards granted", data.rewardCounts?.granted],
  ];

  return (
    <main className="mx-auto w-full max-w-[1600px] px-4 py-8 text-white sm:px-6 lg:px-8">
      <header className="flex flex-wrap items-end justify-between gap-5">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#67A3FA]">Verified growth</p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">Referral operations</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-white/45">Monitor attribution, the verification period, eligibility, and subscription rewards without exposing payment credentials.</p>
        </div>
        <button type="button" onClick={load} className="rounded-full border border-white/15 bg-white/[0.06] px-5 py-2.5 text-sm font-medium text-white/80 hover:border-[#67A3FA]/50 hover:text-white">Refresh</button>
      </header>

      {!data.enabled ? (
        <section className="mt-8 rounded-3xl border border-amber-300/20 bg-amber-300/[0.055] p-6">
          <h2 className="font-semibold text-amber-100">Referral launch is safely disabled</h2>
          <p className="mt-2 text-sm leading-6 text-amber-100/65">No links, rewards, or subscription credits are active. Enable only after web, iOS, Stripe, and App Store validation is complete.</p>
        </section>
      ) : (
        <>
          <section className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {metrics.map(([name, value]) => (
              <div key={name} className="rounded-3xl border border-white/10 bg-[#0D1017] p-5 shadow-[0_18px_55px_rgba(0,0,0,.18)]">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-white/40">{name}</p>
                <p className="mt-3 text-3xl font-semibold">{number(value)}</p>
              </div>
            ))}
          </section>

          <section className="mt-6 overflow-hidden rounded-3xl border border-white/10 bg-[#0D1017]">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 p-5 sm:p-6">
              <div>
                <h2 className="text-xl font-semibold">Referral ledger</h2>
                <p className="mt-1 text-sm text-white/40">{number(data.total)} total records</p>
              </div>
              <label className="flex items-center gap-3 text-sm text-white/55">
                Status
                <select value={status} onChange={(event) => { setStatus(event.target.value); setPage(1); }} className="rounded-xl border border-white/15 bg-[#151923] px-4 py-2.5 text-white outline-none focus:border-[#67A3FA]">
                  <option value="">All</option>
                  <option value="pending_payment">Awaiting payment</option>
                  <option value="holding">Verifying</option>
                  <option value="qualified">Verified</option>
                  <option value="rejected">Rejected</option>
                  <option value="reversed">Reversed</option>
                </select>
              </label>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-white/[0.035] text-[11px] uppercase tracking-[0.14em] text-white/40">
                  <tr>
                    <th className="px-5 py-4">Referrer</th>
                    <th className="px-5 py-4">New officer</th>
                    <th className="px-5 py-4">Status</th>
                    <th className="px-5 py-4">Platform</th>
                    <th className="px-5 py-4">Paid</th>
                    <th className="px-5 py-4">Eligible</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.07]">
                  {data.rows?.length ? data.rows.map((row) => (
                    <tr key={row._id} className="hover:bg-white/[0.025]">
                      <td className="px-5 py-4"><p className="font-medium text-white/85">{row.referrer?.fullname || "—"}</p><p className="mt-1 text-xs text-white/35">{row.referrer?.email || "—"}</p></td>
                      <td className="px-5 py-4"><p className="font-medium text-white/85">{row.referred?.fullname || "—"}</p><p className="mt-1 text-xs text-white/35">{row.referred?.email || "—"}</p></td>
                      <td className="px-5 py-4"><span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold capitalize ${statusStyle[row.status] || statusStyle.pending_payment}`}>{label(row.status)}</span></td>
                      <td className="px-5 py-4 uppercase text-white/55">{row.subscriptionPlatform || "—"}</td>
                      <td className="px-5 py-4 text-white/55">{date(row.firstPaidAt)}</td>
                      <td className="px-5 py-4 text-white/55">{date(row.qualifiesAt || row.qualifiedAt)}</td>
                    </tr>
                  )) : (
                    <tr><td colSpan="6" className="px-5 py-14 text-center text-white/35">No referral records match this view.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between border-t border-white/10 px-5 py-4 text-sm text-white/45">
              <span>Page {page} of {totalPages}</span>
              <div className="flex gap-2">
                <button type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)} className="rounded-xl border border-white/10 px-4 py-2 disabled:opacity-30">Previous</button>
                <button type="button" disabled={page >= totalPages} onClick={() => setPage((value) => value + 1)} className="rounded-xl border border-white/10 px-4 py-2 disabled:opacity-30">Next</button>
              </div>
            </div>
          </section>
        </>
      )}
    </main>
  );
}
